import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Checkbox } from '../../../../components/ui/Checkbox';

export interface ProfileUpdateRow {
  fieldLabel: string;
  currentValue: string;
  newValue: string;
  isReplacing: boolean;
}

interface ProfileUpdatesPanelProps {
  updates: ProfileUpdateRow[];
  skipProfileUpdates: boolean;
  onSkipChange: (skip: boolean) => void;
}

const ProfileUpdatesPanel: React.FC<ProfileUpdatesPanelProps> = ({
  updates,
  skipProfileUpdates,
  onSkipChange,
}) => {
  const { t } = useTranslation();

  if (updates.length === 0) return null;

  return (
    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-2 dark:border-amber-900/50 dark:bg-amber-950/20">
      <div className="flex items-center gap-1.5 text-sm font-medium text-amber-800 dark:text-amber-200">
        <AlertTriangle className="h-4 w-4" />
        {t('profileUpdatesTitle')}
      </div>
      <div className="space-y-1">
        {updates.map((row, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <span className="min-w-[140px] text-muted-foreground">{row.fieldLabel}:</span>
            <span className="text-muted-foreground/60">{row.currentValue || '(empty)'}</span>
            <span className="text-muted-foreground">&rarr;</span>
            <span className="font-medium text-foreground">{row.newValue}</span>
            {row.isReplacing && (
              <span className="flex items-center gap-0.5 text-xs text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-3 w-3" />
                {t('replacing')}
              </span>
            )}
          </div>
        ))}
      </div>
      <Checkbox
        checked={skipProfileUpdates}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSkipChange(e.target.checked)}
        label={t('skipProfileUpdates')}
        size="sm"
      />
    </div>
  );
};

export default ProfileUpdatesPanel;
